import PayableFinance from "component/finance/PayableFinance";
import { ChevronRight } from "lucide-react";
import React, { useEffect } from "react";
import { financeOptions } from "constants/global";
import { useLocation, useNavigate } from "react-router-dom";
import FinanceTopNav from "./FinanceTopNav";
import FinanceSideNav from "./FinanceSideNav";
import { financeTitles } from "constants/FinanceConstants";
import { cn } from "lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "component/ui/Tooltip";

const PayableOptions = ({ handleOptionClick, option, isValid, isProcessing }) => {
  const textToShow = option?.isComingSoon ? "Coming soon" : `You already have a ${isProcessing ? "pending" : "disbursed"} ${option.title}`
    return (
        <Tooltip>
            <TooltipTrigger>
                <div
                    className={cn(
                        ` group cursor-pointer rounded-xl transition-all duration-200 w-full`,
                      isValid ? "opacity-45 cursor-not-allowed" : "hover:bg-light-grey-20"
                    )}
            onClick={() => (!isValid && !option?.isComingSoon) && handleOptionClick(option)}
                >
                    <div className="flex items-center justify-between w-full p-4">
                        <div className="flex items-start space-x-4">
                            <div className="flex items-center justify-center flex-shrink-0 rounded-full size-10 bg-secondary-80">
                                {option.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3
                                    className={`text-base text-left font-[450] mb-1 ${"text-gray-900"}`}
                                >
                                    {option.title}
                                </h3>
                  <p className={`text-sm text-left leading-relaxed ${"text-gray-600"}`}>
                                    {option.description}
                                </p>
                            </div>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                {option?.isComingSoon ? <div

                  className="bg-[#F3F4FB] text-[#494E6E] rounded-[8px] px-2 py-[1px] font-[350] text-[11px] inline-flex  h-[20px] items-center justify-center"
                >
                  Coming soon
                </div> : <ChevronRight
                                className={`w-5 h-5 transition-colors text-gray-400 group-hover:text-gray-600`}
                />}
                        </div>
                    </div>
                </div>
            </TooltipTrigger>
        {(isValid || isProcessing || option?.isComingSoon) && (
                <TooltipContent>
            {textToShow}
                </TooltipContent>
            )}
        </Tooltip>
    );
};

const SubsequentRequest = ({
    paymentMethod,
    setPaymentMethod,
    fileKey,
    setFileKey,
    groupList,
    currentBusinessAccount,
}) => {
    const handleOptionClick = (option) => {
        setPaymentMethod(option.title);
    };
    const handleBack = () => {
        setPaymentMethod(null);
    };
    const navigate = useNavigate();
    const { state } = useLocation();

    useEffect(() => {
        if (state?.financeType) {
            handleOptionClick({ title: financeTitles[state.financeType] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const isInventoryFinanceValid =
      (groupList?.inventory_finance?.filter(
            (item) =>
                item?.status === "disbursed" ||
                item?.status === "approved" ||
                item?.status === "due" ||
                item?.status === "overdue" ||
                item?.status === "pending"
      )?.length || 0) >= (currentBusinessAccount?.financeLimit || 1);

    const isPaymentFinanceValid =
      (groupList?.payment_finance?.filter(
            (item) =>
                item?.status === "disbursed" ||
                item?.status === "approved" ||
                item?.status === "due" ||
                item?.status === "overdue" ||
                item?.status === "pending"
      )?.length || 0) >= (currentBusinessAccount?.financeLimit || 1);

    const isPayableFinanceValid =
      (groupList?.payable_finance?.filter(
            (item) =>
                item?.status === "disbursed" ||
                item?.status === "approved" ||
                item?.status === "due" ||
                item?.status === "overdue" ||
                item?.status === "pending"
      )?.length || 0) >= (currentBusinessAccount?.financeLimit || 1);

    const isReceivableFinanceValid =
      (groupList?.receivable_finance?.filter(
            (item) =>
                item?.status === "disbursed" ||
                item?.status === "approved" ||
                item?.status === "due" ||
                item?.status === "overdue" ||
                item?.status === "pending"
    )?.length || 0) >= (currentBusinessAccount?.financeLimit || 1);

    const inventoryHasProcessingFinance = groupList?.inventory_finance?.some(
        (item) => item?.status === "pending"
    );
    const payableHasProcessingFinance = groupList?.payable_finance?.some(
        (item) => item?.status === "pending"
    );
    const receivableHasProcessingFinance = groupList?.receivable_finance?.some(
        (item) => item?.status === "pending"
    );
    const paymentHasProcessingFinance = groupList?.payment_finance?.some(
        (item) => item?.status === "pending"
    );

    return (
        <>
            {!paymentMethod && (
                <>
                    <FinanceTopNav navigate={navigate} paymentMethod={paymentMethod} />
                    <main className="flex flex-col w-full h-full md:flex-row">
                        <FinanceSideNav paymentMethod={paymentMethod} handleBack={handleBack} />
                        <section className="w-full pb-10 overflow-y-auto md:pl-10 scroll-smooth">
                            <div className="md:w-[594px] md:ml-20 py-10">
                                <h3 className="font-[450] !mb-10 flex items-center justify-center md:justify-start text-left text-gray-10 text-xl md:text-2xl">
                                    What type of finance do you need?
                                </h3>

                  <div className="flex flex-col space-y-2">
                                    {financeOptions.map((option, index) => (
                                        <PayableOptions
                                            key={option.id}
                                            index={index}
                                            handleOptionClick={handleOptionClick}
                                            option={option}
                                            isValid={
                                                option.key === "inventory_finance"
                                                    ? isInventoryFinanceValid
                                                    : option.key === "payable_finance"
                                                      ? isPayableFinanceValid
                                                      : option.key === "payment_finance"
                                                    ? isPaymentFinanceValid
                                                        : isReceivableFinanceValid
                                            }
                                            isProcessing={
                                                option.key === "inventory_finance"
                                                    ? inventoryHasProcessingFinance
                                                    : option.key === "payable_finance"
                                                    ? payableHasProcessingFinance
                                                      : option.key === "payment_finance"
                                                        ? paymentHasProcessingFinance
                                                        : receivableHasProcessingFinance
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        </section>
                    </main>
                </>
            )}

            {paymentMethod && (
                <PayableFinance
                    goBack={handleBack}
                    paymentMethod={paymentMethod}
                    fileKey={fileKey}
                    setFileKey={setFileKey}
                    initialData={state}
                    // hasProcessingFinance={
                    //     paymentMethod === financeOptions[2]?.title
                    //         ? inventoryHasProcessingFinance
                    //         : paymentMethod === financeOptions[0]?.title
                    //         ? payableHasProcessingFinance
                    //         : receivableHasProcessingFinance
                    // }
                    // hasHover={
                    //     paymentMethod === "Inventory finance" && inventoryHasProcessingFinance
                    //         ? "You already have a processing inventory finance"
                    //         : paymentMethod === "Payable finance" &&
                    //           payableHasProcessingFinance
                    //         ? "You already have a processing payable finance"
                    //         : paymentMethod === "Receivable finance" &&
                    //           receivableHasProcessingFinance
                    //         ? "You already have a processing receivable finance"
                    //         : false
                    // }
                />
            )}
        </>
    );
};

export default SubsequentRequest;
